import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import {
  AddExpertiseTextMap,
  ExpertiseItem,
  ExpertiseHeaderNameMap,
  ExpertiseTextMap,
  ExpertiseType,
  ExpertiseNameMap,
  ExpertiseShortTextMap,
  updateCountMap,
  decreaseCountInCountMap,
  FormErrorType,
  keyOfExpertise,
  reorderExpertiseItems,
  updateItemRanks,
} from './APGLUserProfileUtils';
import { ExpertiseSelection } from './ExpertiseSelection';
import { SortableList } from '../v2-common/SortableList';
import * as UtilsApi from '../../api/UtilsApi';

interface Props {
  expertiseType: ExpertiseType;
  items: ExpertiseItem[];
  lastId: number; // Largest ID number that has been used for items in ExpertiseList. It gets increased by 1 for each newly added item.
  expertiseNameMap: Map<string, string>;
  expertiseOptions: SelectProps.Option[];
  yearsOptions: SelectProps.Option[];
  placeholder: string;
  maxCount: number;
  handleExpertiseItemsChanged: (items: ExpertiseItem[]) => void;
  handleExpertiseLastIdUpdated: (id: number) => void;
  handleCountMapUpdated: (map: Map<string, number>, type: FormErrorType) => void;
  handleInputErrorUpdated: (inputError: Map<number, boolean>, type: FormErrorType) => void; // for input validation
}

export const ExpertiseList: React.FunctionComponent<Props> = (props: Props) => {
  const {
    expertiseType,
    items,
    lastId,
    expertiseNameMap,
    expertiseOptions,
    yearsOptions,
    placeholder,
    maxCount,
    handleExpertiseItemsChanged,
    handleExpertiseLastIdUpdated,
    handleCountMapUpdated,
    handleInputErrorUpdated,
  } = props;

  const [expertiseItems, setExpertiseItems] = useState<ExpertiseItem[]>(items);
  const [alert, setAlert] = useState<string>('');
  const [latestId, setLatestId] = useState<number>(lastId);
  const [expertiseCountMap, setExpertiseCoundMap] = useState<Map<string, number>>(() => {
    const initCountMap = new Map<string, number>();
    items.forEach((item) => {
      const { domainExpertise } = item;
      const expertiseCount = initCountMap.has(domainExpertise)
        ? // @ts-expect-error TS2532 - strict migration
          initCountMap.get(domainExpertise) + 1
        : 1;
      initCountMap.set(domainExpertise, expertiseCount);
    });
    return initCountMap;
  }); // expertise->count to track duplication
  const [inputError, setInputError] = useState<Map<number, boolean>>(new Map<number, boolean>()); // id->has error?

  const onExpertiseItemChanged = (item: ExpertiseItem) => {
    const currentItems = [...expertiseItems];
    const index = currentItems.findIndex((expertiseItem) => expertiseItem.id === item.id);
    // @ts-expect-error TS2532 - strict migration
    const oldExpertise = currentItems[index].domainExpertise;
    const newExpertise = item.domainExpertise;
    if (oldExpertise !== newExpertise) {
      // domain expertise has changed, we need to update expertise->count map
      updateCountMap(oldExpertise, newExpertise, expertiseCountMap);
    }
    currentItems[index] = item;
    setExpertiseItems(currentItems);
    handleExpertiseItemsChanged(currentItems);
    setExpertiseCoundMap(expertiseCountMap);
    handleCountMapUpdated(
      expertiseCountMap,
      expertiseType === ExpertiseType.AWSService
        ? FormErrorType.ServiceDuplicationError
        : FormErrorType.DomainDuplicationError,
    );
  };

  const onDeleteExpertise = (id: number) => {
    setAlert('');
    // @ts-expect-error TS2532 - strict migration
    const deletedExpertise = expertiseItems.find((item) => item.id === id).domainExpertise;
    decreaseCountInCountMap(deletedExpertise, expertiseCountMap);
    const currentItems = updateItemRanks(expertiseItems.filter((item) => item.id !== id));
    setExpertiseItems(currentItems);
    handleExpertiseItemsChanged(currentItems);
    setExpertiseCoundMap(expertiseCountMap);
    handleCountMapUpdated(
      expertiseCountMap,
      expertiseType === ExpertiseType.AWSService
        ? FormErrorType.ServiceDuplicationError
        : FormErrorType.DomainDuplicationError,
    );
  };

  const updateInputError = (id: number, expertiseError: string, yearsError: string) => {
    if (expertiseError || yearsError) {
      inputError.set(id, true);
    } else {
      inputError.set(id, false);
    }
    setInputError(inputError);
    handleInputErrorUpdated(
      inputError,
      expertiseType === ExpertiseType.AWSService
        ? FormErrorType.ServiceInputError
        : FormErrorType.DomainInputError,
    );
  };

  const onReorder = (orderedKeys: readonly string[]) => {
    const newItems = reorderExpertiseItems(expertiseItems, orderedKeys);
    setExpertiseItems(newItems);
    handleExpertiseItemsChanged(newItems);
  };

  const expertiseList = (
    <SortableList
      items={expertiseItems}
      keyExtractor={keyOfExpertise}
      onReorder={onReorder}
      item={(item, { setNodeRef, style }) => {
        // Flag duplicates so the selection can show its inline alert.
        const isDuplicated = (expertiseCountMap.get(item.domainExpertise) ?? 0) > 1;
        return (
          <div ref={setNodeRef} style={style}>
            <ExpertiseSelection
              item={item}
              expertiseNameMap={expertiseNameMap}
              expertiseOptions={expertiseOptions}
              yearsOptions={yearsOptions}
              placeholder={placeholder}
              handleExpertiseChanged={onExpertiseItemChanged}
              handleDeleteExpertise={onDeleteExpertise}
              handleInputErrorUpdated={updateInputError}
              duplicationAlert={
                isDuplicated
                  ? `This ${ExpertiseShortTextMap.get(expertiseType)} already exists.`
                  : ''
              }
            />
          </div>
        );
      }}
    />
  );

  const onAddExpertise = () => {
    if (expertiseItems.length >= maxCount) {
      setAlert(`You can only select up to ${maxCount} ${ExpertiseTextMap.get(expertiseType)}.`);
    } else {
      setAlert('');
      const newId = latestId + 1;
      const newItem: ExpertiseItem = {
        // @ts-expect-error TS2322 - strict migration
        domainExpertise: null,
        // @ts-expect-error TS2322 - strict migration
        yearsOfExperience: null,
        id: newId,
        rank: expertiseItems.length,
      };
      const newItems = [...expertiseItems];
      newItems.push(newItem);
      setExpertiseItems(newItems);
      handleExpertiseItemsChanged(newItems);
      setLatestId(newId);
      handleExpertiseLastIdUpdated(newId);
    }
  };

  const onDismissAlert = () => {
    setAlert('');
  };

  const alertSection = alert ? (
    <Alert onDismiss={onDismissAlert} dismissible dismissAriaLabel="Close alert" type="warning">
      {alert}
    </Alert>
  ) : null;

  const addButton = (
    <Button onClick={onAddExpertise}>{AddExpertiseTextMap.get(expertiseType)}</Button>
  );

  const expertiseHeader = (
    <SpaceBetween direction="horizontal" size="xs">
      <Box variant="h3">{ExpertiseHeaderNameMap.get(expertiseType)}</Box>
      <Box variant="p" margin={{ top: 'xxxs' }}>{`(maximum ${UtilsApi.publicationLimit})`}</Box>
    </SpaceBetween>
  );

  return (
    <>
      {expertiseHeader}
      <SpaceBetween direction="vertical" size="s">
        <ColumnLayout columns={3}>
          <div>{ExpertiseNameMap.get(expertiseType)}</div>
          <div>Years of experience</div>
        </ColumnLayout>
        <div>{expertiseList}</div>
        {alertSection}
        {addButton}
      </SpaceBetween>
    </>
  );
};
